// components/marketing/ListFactoryPanel.tsx
'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { toast } from 'sonner';
import { Box, ChevronLeft, Folder, Pencil, Plus, Shapes, Trash2, UserPlus, X } from '@/components/ui/lucide-memo';
import { uploadFileToCloud } from '#lib/azure-client';
import {
  createEmptyListFactoryProfile,
  factoryReferenceUrls,
  factoryReferenceUrlsForFolders,
  type FactoryFolder,
  type FactoryImage,
  type ListFactoryProfile,
} from '#lib/list/factoryProfile';
import { factoryPreviewUrl } from '#lib/list/factoryPreview';
import { chooseFactoryReference } from '#lib/list/factoryNavigation';
import { factoryImageId } from '#lib/list/factoryImageMentions';
import { isWestfieldFactoryPresetFolder } from '#lib/list/westfieldFactoryPreset';
import type { FactoryArchetypeAbstractDirection } from '#lib/list/factoryArchetypeDirection';
import { FactoryPersonaEditor, FactoryPersonaLibrary } from './FactoryPersonaEditor';
import { FactoryObjectEditor, FactoryObjectLibrary } from './FactoryObjectEditor';
import { FactoryImage as FactoryImagePreview } from './FactoryImage';

import { FactoryArchetypeEditor, type ArchetypeReferenceImage } from './FactoryArchetypeEditor';
import type { FactoryObjectDto, FactoryOrganisationProjection, FactoryPersonaDto, KernelHandle, OrganisationRuntimeState } from '#types/types';
import { ICON_BUTTON_CLASS } from '@/components/ui/iconButtonClass';
import { FACTORY_CHIP, FACTORY_GRID, FACTORY_ICON_BUTTON, FACTORY_PILL, FactoryEmpty, FactorySection, FactoryTile } from './factoryUi';

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_BYTES = 20 * 1024 * 1024;
const MAX_REFERENCES = 40;
const MAX_DECODED_PIXELS = 40_000_000;
const IMPORT_CONCURRENCY = 2;
const makeId = (prefix: string) => prefix + '-' + crypto.randomUUID();
const withReferences = (profile: Omit<ListFactoryProfile, 'referenceUrls'>): ListFactoryProfile => ({ ...profile, referenceUrls: factoryReferenceUrls(profile) });
const folderNameFor = (file: File) => {
  const relativePath = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
  const [first] = relativePath.split('/').filter(Boolean);
  return first || 'Reference images';
};
const isInstructionFile = (file: File) => file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt');
const FactoryFolderIcon = ({ className }: { className?: string }) => <svg viewBox="0 0 24 24" className={className} aria-hidden="true"><path fill="currentColor" d="M3 6.75A2.75 2.75 0 0 1 5.75 4h3.2c.73 0 1.42.29 1.94.8l1.36 1.35h6A2.75 2.75 0 0 1 21 8.9v7.35A2.75 2.75 0 0 1 18.25 19H5.75A2.75 2.75 0 0 1 3 16.25z" /></svg>;
const FactoryTextIcon = ({ className }: { className?: string }) => <svg viewBox="0 0 24 24" className={className} aria-hidden="true"><path fill="currentColor" d="M6 2.75A2.75 2.75 0 0 0 3.25 5.5v13A2.75 2.75 0 0 0 6 21.25h12a2.75 2.75 0 0 0 2.75-2.75V8.15L15.35 2.75zm8.15 1.94 4.66 4.66H15.5a1.35 1.35 0 0 1-1.35-1.35zM7 13.25h10v1.5H7zm0 3.5h7v1.5H7z" /></svg>;
type FactoryArchetypeCard = {
  id: string;
  handle: string;
  name: string;
  sourceDescription: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  referenceImages?: ArchetypeReferenceImage[];
  cleanupPending?: boolean;
  revision?: number;
  promptOverride?: string;
  abstractDirection?: FactoryArchetypeAbstractDirection;
};
const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]!, index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  return results;
};
const PROMPT_SECTION_RE = /(\[Colour grade\]\s*:|palette:|light:|materials:|image character:|avoid:|\[User direction\])/gi;
// Section markers of the master prompt stand out as one quiet highlight, not a colour per section.
const PROMPT_MARKER_CLASS = 'rounded bg-white/[0.12] px-1 font-semibold text-[#ECECEC]';
const FactoryPromptEditor = ({ value, onChange, readOnly }: { value: string; onChange: (value: string) => void; readOnly: boolean }) => {
  const overlayRef = useRef<HTMLPreElement>(null);
  return <div className="relative mt-3 min-h-0 flex-1 overflow-hidden rounded-[16px] border border-[#2F2F2F] bg-[#1B1B1B]">
    <pre ref={overlayRef} aria-hidden="true" className="pointer-events-none absolute inset-0 m-0 overflow-hidden whitespace-pre-wrap break-words px-4 py-3 font-mono text-[13px] leading-6 text-white/80">{(() => {
      const parts: React.ReactNode[] = [];
      let offset = 0;
      for (const match of value.matchAll(PROMPT_SECTION_RE)) {
        const index = match.index ?? 0;
        if (index > offset) parts.push(value.slice(offset, index));
        parts.push(<span key={index} className={PROMPT_MARKER_CLASS}>{match[0]}</span>);
        offset = index + match[0].length;
      }
      if (offset < value.length || !parts.length) parts.push(value.slice(offset));
      return parts;
    })()}</pre>
    <textarea value={value} readOnly={readOnly} onChange={(event) => onChange(event.target.value)} onScroll={(event) => {
      if (overlayRef.current) overlayRef.current.scrollTop = event.currentTarget.scrollTop;
    }} data-factory-tour="master-prompt" spellCheck={false} className="absolute inset-0 z-10 h-full w-full resize-none overflow-auto bg-transparent px-4 py-3 font-mono text-[13px] leading-6 text-transparent caret-white outline-none selection:bg-white/25" aria-label="Factory master prompt" />
  </div>;
};
const canDecodeImage = async (file: File): Promise<boolean> => {
  try {
    const image = await createImageBitmap(file);
    try {
      return image.width * image.height <= MAX_DECODED_PIXELS;
    } finally {
      image.close();
    }
  } catch {
    return false;
  }
};

export function ListFactoryPanel({ organisationId, organisationName, factory, kernel, onRefresh, active = true }: {
  organisationId: string;
  organisationName?: string;
  factory: Extract<FactoryOrganisationProjection, { status: 'ready' }>;
  kernel: React.RefObject<KernelHandle | null>;
  onRefresh: () => Promise<boolean>;
  active?: boolean;
}) {
  const ownerId = useKernelStore(state => state.userContext.dbUserId);
  const profile = useMemo(() => withReferences({
    ...createEmptyListFactoryProfile(), folders: factory.referenceFolders, masterPrompt: factory.masterPrompt,
    userInstructions: factory.userInstructions, lastDerivedDirection: factory.lastDerivedDirection,
  }), [factory]);
  const profileRef = useRef(profile);
  useEffect(() => { profileRef.current = profile; }, [profile]);
  const [masterPromptDraft, setMasterPromptDraft] = useState(factory.masterPrompt);
  const [isSavingMasterPrompt, setIsSavingMasterPrompt] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [uploadTargetFolderId, setUploadTargetFolderId] = useState<string | undefined>();
  const [openedFolderId, setOpenedFolderId] = useState<string | undefined>();
  const [openedImageSelection, setOpenedImage] = useState<FactoryImage | null>(null);
  const imageDialog = useRef<HTMLDialogElement>(null);
  const appliedNavigation = useRef<string | undefined>(undefined);
  const navigation = useKernelStore(state => (state.entityKernelState?.[`Organisation:${organisationId}`]?.state?.runtime as OrganisationRuntimeState | undefined)?.factoryNavigation);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showMasterPrompt, setShowMasterPrompt] = useState(false);
  const [editingArchetype, setEditingArchetype] = useState<FactoryArchetypeCard | null | undefined>(undefined);
  const [editingPersona, setEditingPersona] = useState<FactoryPersonaDto | null | undefined>(undefined);
  const [editingObject, setEditingObject] = useState<FactoryObjectDto | null | undefined>(undefined);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const creatingFolderRef = useRef(false);
  const newFolderButtonRef = useRef<HTMLButtonElement>(null);
  const [saveError, setSaveError] = useState('');
  const masterPromptSaveRef = useRef<Promise<void> | null>(null);
  const masterPromptDirtyRef = useRef(false);
  const draftRevisionRef = useRef(factory.revision);
  const [fieldDrafts, setFieldDrafts] = useState<Record<string, { value: string; revision: number }>>({});
  const mountedRef = useRef(true);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const archetypes = factory.archetypes;
  const archetypeRevision = factory.revision;
  const canEditArchetypes = factory.canEdit;
  const sharedReferenceFolders = factory.referenceFolders;
  const refreshArchetypes = onRefresh;

  useEffect(() => {
    if (!active || !navigation || navigation.userId !== ownerId || appliedNavigation.current === navigation.requestId) return;
    appliedNavigation.current = navigation.requestId;
    setOpenedFolderId(navigation.folderId);
    const image = factory.referenceFolders.find(folder => folder.id === navigation.folderId)?.images.find(image => image.id === navigation.imageId);
    setOpenedImage(image ?? null);
    setShowMasterPrompt(false);
    setShowInstructions(false);
  }, [navigation?.requestId, active, ownerId]);
  useEffect(() => {
    const dialog = imageDialog.current;
    if (!dialog || !active || !openedImageSelection) return;
    dialog.showModal();
    return () => { dialog.close(); };
  }, [active, openedImageSelection?.id]);
  const closeImage = () => {
    setOpenedImage(null);
    if (navigation && navigation.imageId === openedImageSelection?.id && navigation.sessionId === kernel.current?.getState().activeChatSessionId && navigation.imageId) {
      kernel.current?.patchEntityRuntimeState('Organisation', organisationId, { factoryNavigation: undefined });
      kernel.current?.patchEntityRuntimeState('ChatSession', navigation.sessionId, { factoryReferenceFocus: factoryImageId({ folderId: navigation.folderId, imageId: navigation.imageId }) });
      kernel.current?.setStudioSection('list');
    }
  };

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => {
    if (!masterPromptDirtyRef.current) setMasterPromptDraft(factory.masterPrompt);
  }, [factory.masterPrompt]);
  useEffect(() => {
    const reload = () => { if (!masterPromptDirtyRef.current && !masterPromptSaveRef.current) void onRefresh(); };
    window.addEventListener('factory-archetypes-changed', reload);
    window.addEventListener('factory-personas-changed', reload);
    window.addEventListener('factory-objects-changed', reload);
    return () => { window.removeEventListener('factory-archetypes-changed', reload); window.removeEventListener('factory-personas-changed', reload); window.removeEventListener('factory-objects-changed', reload); };
  }, [organisationId, onRefresh]);

  const change = async (payload: Record<string, unknown>, expectedRevision = factory.revision) => {
    if (!factory.canEdit || !ownerId || !mountedRef.current) throw new Error('Factory is read-only.');
    return kernel.current!.requestFactoryChange({ organisationId, userId: ownerId, resource: 'profile', method: 'PATCH', payload: { ...payload, expectedRevision } });
  };
  const mutateFolders = async (folders: FactoryFolder[], expectedRevision = factory.revision) => {
    setSaveError('');
    try {
      await change({ referenceFolders: folders }, expectedRevision);
      if (!mountedRef.current) return;
      if (!await onRefresh().catch(() => false)) {
        const message = 'Saved. Reload Factory to see the latest settings.';
        setSaveError(message);
        toast.warning(message);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      const message = error instanceof Error ? error.message : 'Factory could not save. Your draft is kept.';
      setSaveError(message); toast.error(message);
      throw error;
    }
  };
  const closeNewFolder = () => {
    if (creatingFolderRef.current) return;
    setShowNewFolder(false);
    newFolderButtonRef.current?.focus();
  };
  const createFolder = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!factory.canEdit || isImporting || creatingFolderRef.current) return;
    const name = String(new FormData(event.currentTarget).get('folderName') ?? '').trim();
    if (!name) return;
    creatingFolderRef.current = true;
    setIsCreatingFolder(true);
    try {
      await mutateFolders([...profileRef.current.folders, { id: makeId('folder'), name, instructions: '', imagesLocked: false, images: [] }]);
      if (mountedRef.current) {
        setShowNewFolder(false);
        newFolderButtonRef.current?.focus();
      }
    } catch {
      // mutateFolders displays the error; leave the form and its name intact.
    } finally {
      creatingFolderRef.current = false;
      if (mountedRef.current) setIsCreatingFolder(false);
    }
  };
  const regenerate = async () => {
    if (!factory.canEdit || !factoryReferenceUrlsForFolders(factory.referenceFolders).length) return;
    if (masterPromptDirtyRef.current) { setSaveError('Save or discard your draft before refreshing the direction.'); return; }
    setIsRegenerating(true); setSaveError('');
    try { await change({ derive: true }); if (mountedRef.current) await onRefresh(); }
    catch (error) { if (mountedRef.current) setSaveError(error instanceof Error ? error.message : 'Direction refresh failed.'); }
    finally { if (mountedRef.current) setIsRegenerating(false); }
  };
  const changeFieldDraft = (key: string, value: string) => {
    setFieldDrafts(current => ({ ...current, [key]: { value, revision: current[key]?.revision ?? factory.revision } }));
  };
  const clearSavedFieldDraft = (key: string, value: string, revision: number | undefined) => {
    if (!mountedRef.current) return;
    setFieldDrafts(current => {
      if (current[key]?.value !== value || current[key]?.revision !== revision) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };
  const updateFolder = async (folderId: string, field: 'name' | 'instructions', value: string) => {
    const key = 'folder:' + folderId + ':' + field;
    const draft = fieldDrafts[key];
    const revision = draft?.revision;
    const folders = profileRef.current.folders.map(folder => folder.id === folderId ? { ...folder, [field]: value } : folder);
    await mutateFolders(folders, revision);
    clearSavedFieldDraft(key, draft?.value ?? value, revision);
  };

  const removeFolder = useCallback(async (folderId: string) => {
    const current = profileRef.current;
    if (current.folders.find((folder) => folder.id === folderId && isWestfieldFactoryPresetFolder(folder))) {
      toast.error('Westfield Brand is shared across all List projects and always applies.');
      return;
    }
    await mutateFolders(current.folders.filter((folder) => folder.id !== folderId));
  }, [mutateFolders]);

  const updateImage = async (folderId: string, imageId: string, name: string) => {
    const key = 'image:' + imageId + ':name';
    const draft = fieldDrafts[key];
    const revision = draft?.revision;
    await mutateFolders(profileRef.current.folders.map(folder => folder.id === folderId
      ? { ...folder, images: folder.images.map(image => image.id === imageId ? { ...image, name } : image) }
      : folder), revision);
    clearSavedFieldDraft(key, draft?.value ?? name, revision);
  };

  const removeImage = useCallback(async (folderId: string, imageId: string) => {
    const current = profileRef.current;
    await mutateFolders(current.folders.map((folder) => folder.id === folderId
      ? { ...folder, images: folder.images.filter((image) => image.id !== imageId) }
      : folder));
  }, [mutateFolders]);

  const importFiles = useCallback(async (files: File[], targetFolderId?: string) => {
    if (!files.length || !factory.canEdit || !mountedRef.current) return;
    const invalid = files.find((file) => !isInstructionFile(file) && (!ACCEPTED_TYPES.has(file.type) || file.size > MAX_BYTES));
    if (invalid) return toast.error('Use JPG, PNG, or WebP images under 20 MB, plus optional .txt instructions.');
    const images = files.filter((file) => !isInstructionFile(file));
    const current = profileRef.current;
    const targetFolder = targetFolderId ? current.folders.find((folder) => folder.id === targetFolderId) : undefined;
    if (targetFolder?.imagesLocked) return toast.error(targetFolder.name + ' is locked.');
    if (current.referenceUrls.length + images.length > MAX_REFERENCES) return toast.error('Factory supports up to 40 reference images.');

    setIsImporting(true);
    try {
      if (!ownerId || kernel.current?.getState().userContext.dbUserId !== ownerId) throw new Error('Your account changed. Reopen Factory.');
      const instructionsByFolder = new Map<string, string[]>();
      for (const file of files.filter(isInstructionFile)) {
        const key = targetFolderId ?? folderNameFor(file);
        instructionsByFolder.set(key, [...(instructionsByFolder.get(key) ?? []), (await file.text()).trim()]);
      }
      const uploaded = await mapWithConcurrency(images, IMPORT_CONCURRENCY, async (file, index) => {
        if (!mountedRef.current || kernel.current?.getState().userContext.dbUserId !== ownerId) throw new Error('Your account changed. Reopen Factory.');
        const stamp = Date.now() + '-factory-' + index + '-';
        if (!await canDecodeImage(file)) {
          throw new Error(`${file.name} is too large to decode safely or is not a supported image.`);
        }
        const url = await uploadFileToCloud(file, stamp + file.name, file.type, undefined, ownerId);
        if (!url) throw new Error('Factory reference could not be uploaded.');
        return { group: targetFolderId ?? folderNameFor(file), image: { id: makeId('image'), name: file.name, url, createdAt: Date.now() } };
      });
      const byGroup = new Map<string, FactoryImage[]>();
      uploaded.forEach(({ group, image }) => byGroup.set(group, [...(byGroup.get(group) ?? []), image]));
      const groupKeys = new Set([...byGroup.keys(), ...instructionsByFolder.keys()]);
      const folders = targetFolderId
        ? current.folders.map((folder) => folder.id === targetFolderId ? {
          ...folder,
          images: [...folder.images, ...(byGroup.get(targetFolderId) ?? [])],
          instructions: [folder.instructions, ...(instructionsByFolder.get(targetFolderId) ?? [])].filter(Boolean).join('\n\n'),
        } : folder)
        : [...current.folders, ...[...groupKeys].map((name) => ({
          id: makeId('folder'),
          name,
          instructions: (instructionsByFolder.get(name) ?? []).filter(Boolean).join('\n\n'),
          imagesLocked: false,
          images: byGroup.get(name) ?? [],
        }))];
      if (!mountedRef.current) return;
      await mutateFolders(folders, factory.revision);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Factory import failed. Nothing was added.');
    } finally {
      if (mountedRef.current) setIsImporting(false);
    }
  }, [kernel, mutateFolders, ownerId, factory.canEdit, factory.revision]);

  const saveMasterPrompt = async () => {
    if (masterPromptSaveRef.current) return masterPromptSaveRef.current;
    if (!masterPromptDirtyRef.current || !factory.canEdit) return;
    setIsSavingMasterPrompt(true); setSaveError('');
    const draft = masterPromptDraft.trim();
    if (draft === factory.masterPrompt) {
      masterPromptDirtyRef.current = false;
      setMasterPromptDraft(draft); setIsSavingMasterPrompt(false);
      return;
    }
    const save = (async () => {
      try {
        await change({ replaceMasterPrompt: draft }, draftRevisionRef.current);
        if (!mountedRef.current) return;
        masterPromptDirtyRef.current = false;
        if (!await onRefresh()) throw new Error('Saved. Reload Factory to see the latest direction.');
      } catch (error) {
        if (mountedRef.current) setSaveError(error instanceof Error ? error.message : 'The direction could not be saved. Your draft is kept.');
      } finally {
        masterPromptSaveRef.current = null;
        if (mountedRef.current) setIsSavingMasterPrompt(false);
      }
    })();
    masterPromptSaveRef.current = save;
    return save;
  };
  const removeArchetype = async (archetype: FactoryArchetypeCard) => {
    if (!factory.canEdit || !ownerId || !window.confirm('Delete @' + archetype.handle + '?')) return;
    try {
      await kernel.current!.requestFactoryChange({ organisationId, userId: ownerId, resource: 'archetypes', method: 'DELETE', payload: { archetypeId: archetype.id, expectedRevision: factory.revision } });
      if (mountedRef.current) await onRefresh();
    } catch (error) { if (mountedRef.current) setSaveError(error instanceof Error ? error.message : 'Archetype could not be removed.'); }
  };


  const current = profile;
  const currentDirection = current.lastDerivedDirection;
  const effectiveFolders = factory.referenceFolders;
  const hasFactoryReferences = factoryReferenceUrlsForFolders(effectiveFolders).length > 0;
  const openedFolder = effectiveFolders.find((folder) => folder.id === openedFolderId);
  const openedImage = openedFolder?.images.find(image => image.id === openedImageSelection?.id) ?? null;
  const openImageContextMenu = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, image: FactoryImage) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    kernel.current?.setMenuData({
      position: {
        x: event.type === 'click' ? rect.right : 'clientX' in event ? event.clientX || rect.left : rect.left,
        y: event.type === 'click' ? rect.bottom : 'clientY' in event ? event.clientY || rect.top : rect.top,
      },
      actions: [{
        label: 'Add to chat as reference',
        onClick: () => {
          const k = kernel.current;
          const sessionId = k?.getState().activeChatSessionId;
          if (k && sessionId && openedFolder) void chooseFactoryReference(k, sessionId, openedFolder.id, image.id, 'reference').catch(error => k.handleError(error));
        },
      }],
    });
  };
  const draftFeedback = masterPromptDraft !== (profile?.masterPrompt ?? '') && <p className="mb-4 text-[#AFAFAF]" style={{ fontSize: 14 }}>Your unsaved master prompt is preserved. Save it or discard the draft to load shared changes. <button type="button" className="underline" onClick={() => {
          masterPromptDirtyRef.current = false;
          draftRevisionRef.current = factory.revision;
          setMasterPromptDraft(profileRef.current?.masterPrompt ?? '');
          void refreshArchetypes();
        }}>Discard draft and reload</button></p>;
  const saveFeedback = saveError && <p role="alert" className="my-3 text-[#F28B82]" style={{ fontSize: 14 }}>{saveError} <button type="button" className="underline" onClick={() => {
    void onRefresh().then(ok => {
      if (!ok || !mountedRef.current) return;
      const latest = kernel.current?.getInventory().Organisation.registry[organisationId]?.factory;
      if (latest?.status === 'ready') {
        draftRevisionRef.current = latest.revision;
        setFieldDrafts(current => Object.fromEntries(Object.entries(current).map(([key, draft]) => [key, { ...draft, revision: latest.revision }])));
        setSaveError('Latest revision loaded. Review your draft before saving again.');
      }
    });
  }}>Reload Factory</button> {Object.keys(fieldDrafts).length > 0 && <button type="button" className="underline" onClick={() => { setFieldDrafts({}); setSaveError(''); }}>Discard field drafts</button>}</p>;
  const openedPreviewUrl = openedImage ? factoryPreviewUrl(openedImage.url) : null;
  return (
    <section
      data-factory-tour-scope={openedFolder ? 'folder' : 'bank'}
      className="min-h-0 flex-1 overflow-hidden bg-black"
      aria-label="Factory reference bank"
    >
      <div className="h-full overflow-x-hidden overflow-y-auto overscroll-contain px-6 pb-12" style={{ WebkitOverflowScrolling: 'touch' }} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); void importFiles([...event.dataTransfer.files]); }}>
      <div>
        {!openedFolder && factory.canEdit && <>
          <div role="group" aria-label="Factory quick actions" className="-ml-1 mb-8 flex min-w-0 flex-wrap items-center gap-1">
            <button ref={newFolderButtonRef} type="button" data-factory-tour="new-folder" className={FACTORY_CHIP} style={{ fontSize: 14 }} disabled={isImporting} onClick={() => setShowNewFolder(true)}><Folder size={16} />New folder</button>
            <button type="button" data-factory-tour="new-persona" className={FACTORY_CHIP} style={{ fontSize: 14 }} onClick={() => setEditingPersona(null)}><UserPlus size={16} />New persona</button>
            <button type="button" className={FACTORY_CHIP} style={{ fontSize: 14 }} onClick={() => setEditingObject(null)}><Box size={16} />New object</button>
            <button type="button" data-factory-tour="new-archetype" className={FACTORY_CHIP} style={{ fontSize: 14 }} onClick={() => setEditingArchetype(null)}><Shapes size={16} />New archetype</button>
          </div>
          {showNewFolder && <form aria-label="New Factory folder" onSubmit={event => void createFolder(event)} onKeyDown={event => {
            if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); closeNewFolder(); }
          }} className="-mt-4 mb-8 flex flex-wrap items-center gap-2">
            <label className="flex min-w-0 flex-wrap items-center gap-2 text-[#8E8E8E]" style={{ fontSize: 14 }}>Folder name<input name="folderName" autoFocus required maxLength={120} disabled={isCreatingFolder} className="h-8 w-56 min-w-0 max-w-full rounded-full border border-[#2A2A2A] bg-transparent px-3 text-[#ECECEC] outline-none focus:border-[#444444]" style={{ fontSize: 14 }} /></label>
            <button type="submit" className="inline-flex h-8 items-center rounded-full bg-[#F0F0F0] px-3.5 font-medium text-black transition-colors hover:bg-white disabled:opacity-40" style={{ fontSize: 14 }} disabled={isCreatingFolder || isImporting}>{isCreatingFolder ? 'Creating…' : 'Create folder'}</button>
            <button type="button" className={FACTORY_CHIP} style={{ fontSize: 14 }} disabled={isCreatingFolder} onClick={closeNewFolder}>Cancel</button>
          </form>}
        </>}
        {!showMasterPrompt && !showInstructions && !openedImage && saveFeedback}
        {isImporting && <p role="status" className="mb-4 text-[#AFAFAF]" style={{ fontSize: 14 }}>Importing shared references…</p>}
        {!showMasterPrompt && draftFeedback}
        <input ref={imageInputRef} className="hidden" type="file" accept="image/jpeg,image/png,image/webp,.txt" multiple onChange={(event) => { void importFiles([...(event.target.files ?? []),], uploadTargetFolderId); event.target.value = ''; setUploadTargetFolderId(undefined); }} />
        <input ref={folderInputRef} className="hidden" type="file" multiple {...({ webkitdirectory: '' } as React.InputHTMLAttributes<HTMLInputElement>)} onChange={(event) => { void importFiles([...(event.target.files ?? [])]); event.target.value = ''; }} />

        {openedFolderId && !openedFolder && <p role="status" className="mb-4 text-[#AFAFAF]" style={{ fontSize: 14 }}>This folder is no longer available. <button type="button" className="underline" onClick={() => setOpenedFolderId(undefined)}>Back to Factory</button></p>}
        {!openedFolder ? (<>
          <FactorySection id="factory-folders" first title="Brand"
            description={sharedReferenceFolders.length > 0 ? `Shared across all ${organisationName ?? 'Westfield'} List projects; applies to every generation.` : 'Reference folders and the creative direction'}>
            <div className={FACTORY_GRID}>
              {effectiveFolders.map((folder) => (
                <article data-factory-tour="folder" key={folder.id} className="group flex h-[72px] min-w-0 cursor-pointer items-center gap-3 rounded-[16px] bg-[#1B1B1B] pl-4 pr-3 text-left transition-colors hover:bg-[#232323]" onClick={() => { setOpenedFolderId(folder.id); }} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); event.stopPropagation(); void importFiles([...event.dataTransfer.files], folder.id); }}>
                  <FactoryFolderIcon className="h-6 w-6 shrink-0 text-[#8E8E8E]" />
                  <span className="min-w-0 flex-1">
                    <input value={fieldDrafts['folder:' + folder.id + ':name']?.value ?? folder.name} onChange={event => changeFieldDraft('folder:' + folder.id + ':name', event.target.value)} disabled={!factory.canEdit || folder.imagesLocked} onClick={(event) => event.stopPropagation()} onBlur={(event) => { if (event.target.value.trim() && event.target.value !== folder.name) void updateFolder(folder.id, 'name', event.target.value.trim()).catch(() => undefined); }} className="block w-full min-w-0 bg-transparent font-medium text-[#ECECEC] outline-none disabled:cursor-pointer" style={{ fontSize: 14 }} aria-label="Folder name" data-factory-tour={folder.imagesLocked ? undefined : "folder-name"} />
                    <span className="block text-[#8E8E8E]" style={{ fontSize: 13 }}>{folder.images.length} image{folder.images.length === 1 ? '' : 's'}{folder.imagesLocked ? ' · Locked' : ''}</span>
                  </span>
                  {factory.canEdit && !folder.imagesLocked && <button type="button" onClick={(event) => { event.stopPropagation(); void removeFolder(folder.id).catch(() => undefined); }} className={`${FACTORY_ICON_BUTTON} opacity-0 group-hover:opacity-100 focus-visible:opacity-100`} data-factory-tour="folder-delete" aria-label={'Delete ' + folder.name}><Trash2 size={15} /></button>}
                </article>
              ))}
              <button data-factory-tour="direction" type="button" onClick={() => setShowMasterPrompt(true)} className="flex h-[72px] min-w-0 items-center gap-3 rounded-[16px] bg-[#1B1B1B] px-4 text-left transition-colors hover:bg-[#232323]">
                <FactoryTextIcon className="h-6 w-6 shrink-0 text-[#8E8E8E]" />
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-[#ECECEC]" style={{ fontSize: 14 }}>Creative direction</span>
                  <span className="block truncate text-[#8E8E8E]" style={{ fontSize: 13 }}>{(current.masterPrompt.trim() || currentDirection?.prompt.trim() || 'No direction text yet.').replace(/\s+/g, ' ')}</span>
                </span>
              </button>
            </div>
          </FactorySection>
            <div data-factory-tour="personas"><FactoryPersonaLibrary organisationId={organisationId} onRefresh={onRefresh} active={active} onEdit={setEditingPersona} /></div>
            <FactoryObjectLibrary organisationId={organisationId} onRefresh={onRefresh} active={active} onEdit={setEditingObject} />
            <FactorySection data-factory-tour="archetypes" id="factory-archetypes" title="Archetypes" description="Image-derived concepts within the Westfield brand"
              action={canEditArchetypes ? <button type="button" data-factory-tour="add-archetype" onClick={() => setEditingArchetype(null)} className={FACTORY_PILL} style={{ fontSize: 14 }}><Plus size={16} />Add archetype</button> : undefined}>
              {archetypes.length === 0 ? <FactoryEmpty>Create one with <code className="font-mono text-[#ECECEC]">/archetype: &quot;name&quot;</code> and one or more reference images.</FactoryEmpty> : <div className={FACTORY_GRID}>
                {archetypes.map((archetype) => {
                  const direction = archetype.abstractDirection;
                  const previewUrl = archetype.thumbnailUrl ?? archetype.imageUrl;
                  const parameters = direction ? [
                    ['Subjects', (direction.subjectCount || 'No visible') + ' ' + direction.subjectCategory],
                    ['Environment', direction.environmentCategory],
                    ['Action', direction.actionTension],
                    ['Mood', direction.mood],
                  ] : [];
                  return (
                    <FactoryTile key={archetype.id}
                      picture={<FactoryImagePreview src={previewUrl} alt={archetype.name} className="h-full w-full" />}
                      name={archetype.name}
                      meta={archetype.cleanupPending && canEditArchetypes ? `@${archetype.handle} · Removal cleanup pending` : `@${archetype.handle}`}
                      openProps={{ 'aria-label': 'Open ' + archetype.name, onClick: () => setEditingArchetype(archetype), disabled: !canEditArchetypes }}
                      actions={canEditArchetypes ? <><button type="button" onClick={() => setEditingArchetype(archetype)} className={FACTORY_ICON_BUTTON} data-factory-tour="edit-archetype" aria-label={'Edit ' + archetype.name}><Pencil size={14} /></button><button type="button" onClick={() => void removeArchetype(archetype)} className={FACTORY_ICON_BUTTON} data-factory-tour="delete-archetype" aria-label={'Delete ' + archetype.name}><Trash2 size={14} /></button></> : undefined}
                      footer={<details className="mt-1 px-1">
                        <summary data-factory-tour="parameters" className="cursor-pointer text-[#8E8E8E] hover:text-[#ECECEC]" style={{ fontSize: 13 }}>Show Factory parameters</summary>
                        <div className="mt-2 space-y-3">
                          <p className="text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>{archetype.sourceDescription}</p>
                          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1" style={{ fontSize: 13, lineHeight: '18px' }}>
                            {parameters.map(([label, value]) => <React.Fragment key={label}><dt className="text-[#8E8E8E]">{label}</dt><dd className="text-[#ECECEC]">{value}</dd></React.Fragment>)}
                            {archetype.revision ? <><dt className="text-[#8E8E8E]">Revision</dt><dd className="text-[#ECECEC]">{archetype.revision}</dd></> : null}
                          </dl>
                          {archetype.attachedPrompt && <div><p className="text-[#8E8E8E]" style={{ fontSize: 13 }}>Prompt added when attached</p><p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap break-words text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>{archetype.attachedPrompt}</p></div>}
                        </div>
                      </details>}
                    />
                  );
                })}
              </div>}
            </FactorySection>
          </>
        ) : (
          <div onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); void importFiles([...event.dataTransfer.files], openedFolder.id); }}>
            <button type="button" onClick={() => setOpenedFolderId(undefined)} className={`-ml-1 mb-8 ${FACTORY_CHIP}`} style={{ fontSize: 14 }} aria-label="Back to Factory" data-factory-tour="back">
              <ChevronLeft size={16} /> Factory
            </button>
            <FactorySection data-factory-tour="drop-files" id="factory-files" first title={openedFolder.name}
              description={`${openedFolder.images.length + 1} files · ${openedFolder.imagesLocked ? 'Locked' : 'Drop images here'}`}>
              <div className={FACTORY_GRID}>
                <FactoryTile
                  picture={<span className="flex h-full w-full flex-col justify-center gap-3 px-6"><span className="h-2 w-full rounded-full bg-white/[0.14]" /><span className="h-2 w-[88%] rounded-full bg-white/[0.10]" /><span className="h-2 w-[72%] rounded-full bg-white/[0.10]" /><span className="h-2 w-[81%] rounded-full bg-white/[0.10]" /></span>}
                  name="Folder instructions"
                  meta={openedFolder.instructions.trim() ? openedFolder.instructions.trim().replace(/\s+/g, ' ') : 'No instructions yet'}
                  openProps={{ 'data-factory-tour': 'instructions', 'aria-label': 'Folder instructions', onClick: () => setShowInstructions(true) }}
                />
                {openedFolder.images.map((image) => (
                  <FactoryTile key={image.id}
                    picture={<FactoryImagePreview src={image.thumbnailUrl ?? factoryPreviewUrl(image.url) ?? image.url} alt={image.name} className="h-full w-full" />}
                    name={image.name}
                    openProps={{ 'data-factory-tour': 'image', 'aria-label': 'Open image ' + image.name, onClick: () => { setOpenedImage(image); }, onContextMenu: (event) => openImageContextMenu(event, image), onKeyDown: (event) => { if (event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10')) openImageContextMenu(event, image); } }}
                    actions={<button type="button" data-factory-tour="image-options" aria-label={'More options for image ' + image.name} aria-haspopup="menu" onClick={(event) => openImageContextMenu(event, image)} className={FACTORY_ICON_BUTTON}><span aria-hidden="true" style={{ fontSize: 16 }}>⋯</span></button>}
                  />
                ))}
              </div>
            </FactorySection>
          </div>
        )}

        {editingPersona !== undefined && <FactoryPersonaEditor key={organisationId + (editingPersona?.id ?? 'new')} persona={editingPersona ? factory.personas.find(persona => persona.id === editingPersona.id) ?? editingPersona : editingPersona} organisationId={organisationId} revision={factory.revision} canEdit={factory.canEdit} onRefresh={onRefresh} onSaved={setEditingPersona} onClose={() => setEditingPersona(undefined)} />}
        {editingObject !== undefined && <FactoryObjectEditor key={organisationId + (editingObject?.id ?? 'new')} object={editingObject ? factory.objects?.find(object => object.id === editingObject.id) ?? editingObject : null} organisationId={organisationId} revision={factory.objectsRevision ?? 0} canEdit={factory.canEdit} onRefresh={onRefresh} onSaved={setEditingObject} onClose={() => setEditingObject(undefined)} />}
        {editingArchetype !== undefined && <FactoryArchetypeEditor key={organisationId + (editingArchetype?.id ?? 'new')} archetype={editingArchetype} organisationId={organisationId} revision={archetypeRevision} onRefresh={refreshArchetypes} onSaved={setEditingArchetype} onClose={() => setEditingArchetype(undefined)} />}

        {showMasterPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-factory-tour-scope="direction" role="dialog" aria-modal="true" onClick={(event) => { if (event.target === event.currentTarget) setShowMasterPrompt(false); }}>
            <div className="flex h-[min(680px,80vh)] w-full max-w-3xl flex-col rounded-[24px] border border-white/[0.06] bg-[#212121] p-5 shadow-2xl">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>Creative direction</span>
                <button type="button" onClick={() => setShowMasterPrompt(false)} className={`ml-auto ${ICON_BUTTON_CLASS}`} aria-label="Close"><X size={18} /></button>
              </div>
              <label className="mt-4 text-[#8E8E8E]" style={{ fontSize: 13 }}>Master prompt: derived from the reference bank and editable</label>
              {saveFeedback}
              {draftFeedback}
              <FactoryPromptEditor value={masterPromptDraft} readOnly={!factory.canEdit || isSavingMasterPrompt || isRegenerating} onChange={value => {
                if (!factory.canEdit || isSavingMasterPrompt || isRegenerating) return;
                if (!masterPromptDirtyRef.current) draftRevisionRef.current = factory.revision;
                masterPromptDirtyRef.current = value !== (profileRef.current?.masterPrompt ?? '');
                setMasterPromptDraft(value);
              }} />
              <p className="mt-3 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>Edit each section directly. Saving updates the creative direction. Refresh it after changing your reference images.{currentDirection ? ` ${currentDirection.referenceCount} reference images · Updated ${new Date(currentDirection.analyzedAt).toLocaleString()}.` : ''}</p>
              {factory.feedbackLessons && <p className="mt-3 max-h-28 overflow-y-auto whitespace-pre-wrap text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}><span className="block text-[#8E8E8E]">Learned from Good and Bad results</span>{factory.feedbackLessons.replace('[Brand feedback lessons]\n', '')}</p>}
              <div className="mt-4 flex items-center justify-end gap-2">
                <button data-factory-tour="refresh-direction" type="button" disabled={!factory.canEdit || !hasFactoryReferences || isRegenerating || isSavingMasterPrompt} onClick={() => void regenerate()} className={FACTORY_PILL} style={{ fontSize: 14 }}>{isRegenerating ? 'Refreshing from files…' : 'Refresh derived prompt from files'}</button>
                <button data-factory-tour="save-direction" type="button" disabled={!factory.canEdit || isSavingMasterPrompt || isRegenerating} onClick={() => void saveMasterPrompt().catch(error => toast.error(error instanceof Error ? error.message : 'Factory master prompt could not be saved.'))} className="inline-flex h-8 items-center rounded-full bg-[#F0F0F0] px-3.5 font-medium text-black transition-colors hover:bg-white disabled:opacity-40" style={{ fontSize: 14 }}>{isSavingMasterPrompt ? 'Saving…' : 'Save master prompt'}</button>
              </div>
            </div>
          </div>
        )}
        {showInstructions && openedFolder && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" role="dialog" aria-modal="true"><div className="flex h-[min(680px,80vh)] w-full max-w-3xl flex-col rounded-[24px] border border-white/[0.06] bg-[#212121] p-5 shadow-2xl"><div className="flex items-center gap-2"><span className="font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>Folder instructions</span><button type="button" onClick={() => setShowInstructions(false)} className={`ml-auto ${ICON_BUTTON_CLASS}`} aria-label="Close"><X size={18} /></button></div>{saveFeedback}<textarea value={fieldDrafts['folder:' + openedFolder.id + ':instructions']?.value ?? openedFolder.instructions} onChange={event => changeFieldDraft('folder:' + openedFolder.id + ':instructions', event.target.value)} disabled={!factory.canEdit || openedFolder.imagesLocked} onBlur={(event) => { if (event.target.value !== openedFolder.instructions) void updateFolder(openedFolder.id, 'instructions', event.target.value).catch(() => undefined); }} placeholder="Describe this folder's direction" className="mt-4 min-h-0 flex-1 resize-none rounded-[16px] border border-[#2F2F2F] bg-[#1B1B1B] p-4 leading-6 text-[#ECECEC] outline-none placeholder:text-[#8E8E8E] focus:border-[#444444] disabled:cursor-not-allowed" style={{ fontSize: 14 }} /></div></div>}
        {openedImage && openedFolder && (
          <dialog ref={imageDialog} aria-label={`Factory image: ${openedImage.name}`} className="fixed inset-0 z-50 m-auto max-h-[95dvh] w-[95vw] max-w-5xl rounded-2xl bg-transparent p-0 text-[#ECECEC] backdrop:bg-black/80" onCancel={event => { event.preventDefault(); closeImage(); }} onClick={event => { if (event.target === event.currentTarget) closeImage(); }}>
            <div className="flex max-h-full w-full max-w-5xl flex-col rounded-[24px] border border-white/[0.06] bg-[#212121] p-4 shadow-2xl" onClick={(event) => event.stopPropagation()}>
              <div className="mb-3 flex items-center gap-3">
                <input value={fieldDrafts['image:' + openedImage.id + ':name']?.value ?? openedImage.name} onChange={event => changeFieldDraft('image:' + openedImage.id + ':name', event.target.value)} disabled={!factory.canEdit || openedFolder.imagesLocked} onBlur={(event) => { if (event.target.value.trim() && event.target.value !== openedImage.name) void updateImage(openedFolder.id, openedImage.id, event.target.value.trim()).catch(() => undefined); }} className="min-w-0 flex-1 bg-transparent font-semibold text-[#ECECEC] outline-none disabled:cursor-default" style={{ fontSize: 16 }} aria-label="Image name" />
                <a href={openedImage.url} target="_blank" rel="noreferrer" className={FACTORY_CHIP} style={{ fontSize: 14 }}>Open original</a>
                <button type="button" onClick={() => {
                  const k = kernel.current;
                  const sessionId = k?.getState().activeChatSessionId;
                  if (k && sessionId) void chooseFactoryReference(k, sessionId, openedFolder.id, openedImage.id, 'reference').then(() => setOpenedImage(null)).catch(error => k.handleError(error));
                }} className={FACTORY_PILL} style={{ fontSize: 14 }}>@ Reference</button>
                <button type="button" autoFocus onClick={closeImage} className={ICON_BUTTON_CLASS} aria-label="Close"><X size={18} /></button>
              </div>
              {saveFeedback}
              <div key={openedImage.id} onContextMenu={(event) => { imageDialog.current?.close(); setOpenedImage(null); openImageContextMenu(event, openedImage); }} className="relative h-[calc(100dvh-180px)] max-h-[720px] min-h-0 w-full">
                <FactoryImagePreview src={openedPreviewUrl ?? undefined} thumbnailSrc={openedImage.thumbnailUrl} alt={openedImage.name} eager fit="contain" errorMessage="Preview unavailable. Reopen to retry, or open the original." className="h-full w-full" />
              </div>
              {factory.canEdit && !openedFolder.imagesLocked && <button type="button" onClick={() => { void removeImage(openedFolder.id, openedImage.id).catch(() => undefined); setOpenedImage(null); }} className="mt-3 inline-flex h-8 items-center self-start rounded-full border border-[#2F2F2F] px-3 text-[#F28B82] transition-colors hover:bg-white/[0.06]" style={{ fontSize: 14 }}>Remove image</button>}
            </div>
          </dialog>
        )}
      </div>
      </div>
    </section>
  );
}
